import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, getSession, signInWithEmail, signOut as supabaseSignOut, getUserRole, setupAuthListener } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { useNotification } from './NotificationContext';
import LoadingSpinner from '../components/LoadingSpinner';

interface AuthContextType {
  user: User | null;
  userRole: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotification();

  const handleAuthStateChange = useCallback(async (currentUser: User | null) => {
    try {
      if (!currentUser) {
        setUser(null);
        setUserRole(null);
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
        return;
      }

      // Force bypass cache when getting user role
      const role = await getUserRole(currentUser.id, true);
      setUser(currentUser);
      setUserRole(role);

      if (location.pathname === '/login') {
        const from = location.state?.from || '/';
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Erreur de mise à jour de l\'état auth:', error);
      setUser(null);
      setUserRole(null);
      addNotification('error', 'Erreur d\'authentification');
      navigate('/login', { replace: true });
    }
  }, [navigate, location, addNotification]);

  const checkSession = useCallback(async () => {
    try {
      setLoading(true);
      const session = await getSession();
      
      if (session?.user) {
        // Force bypass cache when checking session
        const role = await getUserRole(session.user.id, true);
        setUser(session.user);
        setUserRole(role);
      } else {
        await handleAuthStateChange(null);
      }
    } catch (error) {
      console.error('Erreur de vérification de session:', error);
      await handleAuthStateChange(null);
    } finally {
      setLoading(false);
    }
  }, [handleAuthStateChange]);

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        setLoading(true);
        const session = await getSession();
        
        if (session?.user && mounted) {
          await handleAuthStateChange(session.user);
        } else if (mounted) {
          await handleAuthStateChange(null);
        }
      } catch (error) {
        console.error('Erreur d\'initialisation auth:', error);
        if (mounted) {
          await handleAuthStateChange(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    const { data: authListener } = setupAuthListener(async (session) => {
      if (mounted) {
        if (session?.user) {
          await handleAuthStateChange(session.user);
        } else {
          await handleAuthStateChange(null);
        }
      }
    });

    return () => {
      mounted = false;
      authListener?.subscription.unsubscribe();
    };
  }, [handleAuthStateChange]);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { user: authUser } = await signInWithEmail(email, password);
      
      if (!authUser) {
        throw new Error('Échec de la connexion');
      }

      await handleAuthStateChange(authUser);
    } catch (error: any) {
      console.error('Erreur de connexion:', error);
      addNotification('error', error.message || 'Erreur de connexion');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      // Clear local state first
      setUser(null);
      setUserRole(null);
      
      // Then attempt to sign out from Supabase
      await supabaseSignOut();
      
      // Navigate regardless of signout success
      navigate('/login', { replace: true });
    } catch (error: any) {
      console.error('Erreur de déconnexion:', error);
      // Even if there's an error, we want to clear the local state and redirect
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <AuthContext.Provider value={{
      user,
      userRole,
      loading,
      signIn,
      signOut,
      checkSession
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
}